import { useState, useCallback } from "preact/hooks";
import type { PageContext, WidgetMode } from "@pankagent/shared";
import type { DisplayMessage } from "../types/widget";
import { initSession as apiInitSession } from "../services/chatApi";

export interface UseChatSessionOptions {
  apiUrl: string;
  tenantId: string;
  visitorId?: string;
  welcomeMessage?: string;
  /** Widget mode - used to prefix session ID for independence */
  mode?: WidgetMode;
}

export interface UseChatSessionReturn {
  sessionId: string | null;
  isInitialized: boolean;
  isInitializing: boolean;
  initSession: (pageContext: PageContext) => Promise<DisplayMessage | null>;
  setSessionId: (id: string) => void;
}

/**
 * Generate a prefixed visitor ID based on widget mode
 * This ensures overlay and embedded widgets have separate sessions
 */
function getPrefixedVisitorId(
  visitorId: string | undefined,
  mode: WidgetMode
): string | undefined {
  if (!visitorId) return undefined;
  return `${mode}_${visitorId}`;
}

/**
 * Hook to manage chat session lifecycle
 */
export function useChatSession(
  options: UseChatSessionOptions
): UseChatSessionReturn {
  const {
    apiUrl,
    tenantId,
    visitorId,
    welcomeMessage: fallbackWelcome,
    mode = "overlay",
  } = options;

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  // Prefix visitor ID with mode for session independence
  const prefixedVisitorId = getPrefixedVisitorId(visitorId, mode);

  const initSession = useCallback(
    async (pageContext: PageContext): Promise<DisplayMessage | null> => {
      if (isInitialized || sessionId || isInitializing) {
        return null;
      }

      setIsInitializing(true);

      try {
        const data = await apiInitSession(
          { apiUrl, tenantId },
          pageContext,
          prefixedVisitorId
        );

        setSessionId(data.sessionId);
        setIsInitialized(true);

        return {
          id: data.messageId,
          role: "assistant",
          content: data.welcomeMessage,
        };
      } catch (error) {
        console.error("Failed to initialize session:", error);
        setIsInitialized(true);

        // Return fallback welcome message
        return {
          id: "welcome",
          role: "assistant",
          content: fallbackWelcome || "Bonjour ! Comment puis-je vous aider ?",
        };
      } finally {
        setIsInitializing(false);
      }
    },
    [
      apiUrl,
      tenantId,
      prefixedVisitorId,
      fallbackWelcome,
      isInitialized,
      sessionId,
      isInitializing,
    ]
  );

  return {
    sessionId,
    isInitialized,
    isInitializing,
    initSession,
    setSessionId,
  };
}
